import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import ListView from "../../../utils/Custom/listView";
import ListSearchSection from "../../../utils/Custom/ListSearchSection";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { submitSiteDelete } from "../../../utils/commonComponent/delete";
import { AddButton } from "../../../utils/Custom/Button";
import { clienTypes } from "../../../utils/commonComponent/common";
import { getPermissions } from "../rbac/hasAccess";

const headers = ["id", "clientType", "Client", "address", "gstNumber", "uniCode"];

const fields = [
  { value: "clientType", label: "Client Type" },
  { value: "Client", label: "Client" },
  { value: "address", label: "Address" },
  { value: "gstNumber", label: "GST Number" },
  { value: "uniCode", label: "UniCode" },
];

const ClientMasterList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);
  const handleEdit = (row) => navigate(`/master/clients/${row.id}`);
  const handleDelete = (row) => submitSiteDelete("clients", row.id);

  const [clientList, setClientList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [allClientsData, setAllClientsData] = useState([]);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "clientType");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    // fetch new data from backend here
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    let whereConditions = "";

    const colMap = {
      clientType: "cl.type",
      type: "cl.type",
      Client: "cl.companyName",
      address: "addr.address",
      gstNumber: "cl.gstNumber",
      uniCode: "cl.uniCode",
    };
    const colName = colMap[searchBy];
    if ((searchBy === "clientType" || searchBy === "type") && searchKey.trim()) {
      const key = searchKey.toLowerCase();
      const typeObj = clienTypes.find(t =>
        t.label.toLowerCase().includes(key) || t.short.toLowerCase().includes(key) || (t.code && t.code.toLowerCase().includes(key))
      );

      whereConditions = typeObj ? ` AND cl.type = ${typeObj.value}` : ` AND 1=0`;
    }
    else if (colName && searchKey.trim()) {
      whereConditions = ` AND LOWER(${colName}) LIKE LOWER('%${searchKey.trim()}%')`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${rowsPerPage * Number(currentPage)}`;
    }
    return `SELECT cl.*, cl.companyName AS Client, addr.address AS address FROM clients cl LEFT JOIN addresses addr ON SUBSTRING_INDEX(cl.address, ',', 1) = addr.id WHERE cl.status = 1 ${whereClause} ORDER BY cl.id DESC ${paginationSettings};`;
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(*) AS totalRows FROM clients cl LEFT JOIN addresses addr ON SUBSTRING_INDEX(cl.address, ',', 1) = addr.id WHERE cl.status = 1 ${whereClause};`;
  };

  const fetchAllClientsData = () => {
    const whereClause = getWhereClause();
    return `SELECT cl.*, cl.companyName AS Client, addr.address AS address FROM clients cl LEFT JOIN addresses addr ON SUBSTRING_INDEX(cl.address, ',', 1) = addr.id WHERE cl.status = 1 ${whereClause} ORDER BY cl.id DESC;`;
  };

  const fetchData = async () => {
    try {
      const [ClientList, countResult, allData] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
        fetchQuery(fetchAllClientsData()),
      ]);

      const clientData = ClientList || [];
      const mappedData = clientData.map((item) => {
        const typeObj = clienTypes.find((ct) => ct.value === item.type);
        return {
          ...item,
          clientType: typeObj ? typeObj.label : (item.type || ""),
          type: typeObj ? typeObj.label : (item.type || ""),
          address: item.address || "",
        };
      });

      const allClientData = allData || [];
      const mappedAllData = allClientData?.map((item) => {
        const typeObj = clienTypes.find((ct) => ct.value === item.type);
        return {
          clientType: typeObj ? typeObj.label : (item.type || ""),
          companyName: item.companyName || "",
          address: item.address || "",
          gstNumber: item.gstNumber || "",
          uniCode: item.uniCode || "",
        };
      });

      setClientList(mappedData);
      setTotalRows(countResult?.[0]?.totalRows || 0);
      setAllClientsData(mappedAllData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    // Save search state to sessionStorage
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("clientType");
    setSearchKey("");
    // Clear all employee list session storage
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (searched == false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-outline"
          to="/master/clients/0"
          label="Add Client"
          icon={<AddIcon fontSize="small" />}
        />
      )}

      <ListSearchSection
        title="Search Client"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
        showExcelDownload={permissions.excelDownload || permissions.allAccess}
        finalData={allClientsData}
        notNeededColumn={["id", "type", "contact", "status", "signature", "logopath", "vendorId", "branch", "shippingAddress", "location", "Client", "email", "state", "lastModified"]}
      />

      <ListView
        headers={headers}
        data={clientList}
        actions={{
          edit: handleEdit,
          delete: handleDelete,
        }}
        showCheckbox={true} //enable checkbox
        deleteTableName={"clients"}
        moduleId={props.moduleId}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default ClientMasterList;
